// Mikrofonpegel für die Leiste: kleiner Speicher (ohne Re-Render des ganzen Kontexts) für den Pegel während des
// Gesprächs und ein Messer für den Pegel VOR dem Anruf (eigener Mikrofon-Stream, nur solange das Menü offen ist).
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { mikrofonHilfeBeiSperre } from "./desktop";
import { pegelUrteil, rmsDbfs } from "./logik";

let wert: number | null = null;
const hoerer = new Set<() => void>();

/** Vom Provider während des Gesprächs gesetzt (dBFS des Mikrofons, null = nicht gemessen). */
export function setzeMikrofonDb(db: number | null): void {
  const neu = db === null ? null : Number.isFinite(db) ? Math.round(db) : -120;
  if (neu === wert) return;
  wert = neu;
  hoerer.forEach((h) => h());
}

export function useMikrofonDb(): number | null {
  return useSyncExternalStore(
    (h) => {
      hoerer.add(h);
      return () => hoerer.delete(h);
    },
    () => wert,
  );
}

/** Audio-Constraints wie im Gespräch: Echo, Rauschen und automatische Verstärkung ausdrücklich an. */
export const MIKRO_CONSTRAINTS = { echoCancellation: true, noiseSuppression: true, autoGainControl: true } as const;

/**
 * Pegel vor dem Anruf: öffnet das gewählte Mikrofon, solange `aktiv` gilt, misst alle 150 ms und merkt die
 * Spitze der letzten 5 s. Gibt dB (aktuell), Spitze, Urteil und einen Fehlertext zurück.
 */
export function useMikrofonTest(aktiv: boolean, geraeteId: string | null): { db: number | null; spitze: number | null; urteil: ReturnType<typeof pegelUrteil>; fehler: string | null } {
  const [db, setDb] = useState<number | null>(null);
  const [spitze, setSpitze] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const proben = useRef<{ t: number; db: number }[]>([]);

  useEffect(() => {
    if (!aktiv || !navigator.mediaDevices?.getUserMedia) {
      setDb(null);
      setSpitze(null);
      return;
    }
    let weg = false;
    let strom: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let takt = 0;
    proben.current = [];
    setFehler(null);
    navigator.mediaDevices
      .getUserMedia({ audio: { ...MIKRO_CONSTRAINTS, ...(geraeteId ? { deviceId: { exact: geraeteId } } : {}) } })
      .then((s) => {
        if (weg) {
          s.getTracks().forEach((x) => x.stop());
          return;
        }
        strom = s;
        ctx = new AudioContext();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        ctx.createMediaStreamSource(s).connect(analyser);
        const daten = new Float32Array(2048);
        takt = window.setInterval(() => {
          analyser.getFloatTimeDomainData(daten);
          const w = rmsDbfs(daten);
          const jetzt = Date.now();
          proben.current.push({ t: jetzt, db: w });
          while (proben.current.length && jetzt - proben.current[0].t > 5000) proben.current.shift();
          setDb(Number.isFinite(w) ? Math.round(w) : -120);
          const m = proben.current.reduce((a, p) => Math.max(a, p.db), -Infinity);
          setSpitze(Number.isFinite(m) ? Math.round(m) : -120);
        }, 150);
      })
      .catch((e: Error) => {
        mikrofonHilfeBeiSperre(e);
        setFehler(e.name === "NotAllowedError" ? "Mikrofon nicht freigegeben." : "Mikrofon lässt sich nicht öffnen.");
      });
    return () => {
      weg = true;
      window.clearInterval(takt);
      strom?.getTracks().forEach((x) => x.stop());
      void ctx?.close();
    };
  }, [aktiv, geraeteId]);

  // Urteil erst, wenn etwa 3 s gemessen wurden (Zeit zum Sprechen)
  const [reif, setReif] = useState(false);
  useEffect(() => {
    setReif(false);
    if (!aktiv) return;
    const t = window.setTimeout(() => setReif(true), 3000);
    return () => window.clearTimeout(t);
  }, [aktiv, geraeteId]);

  return { db, spitze, urteil: reif ? pegelUrteil(spitze) : "unbekannt", fehler };
}
